import {domToReact, HTMLReactParserOptions, Element as ParserElement} from 'html-react-parser';
import {Fragment, useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {clientActionsApi} from 'api';
import {Button, ClientDynamicContent, DynamicContent, LanguageSwitcher, NodeElement, PredefinedNotifierType} from 'components';
import {DynamicComponentNames, PreSessionFlowUnits as Units, PRE_SESSION_FLOW_DEFAULT_TEMPLATE} from 'constants/client';
import {ClientContext} from 'context';
import {useApiData, useClientSession} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {
    PRE_SESSION_FLOW_LIMITS, PreSessionFlowCompletion, PreSessionFlowState, PreSessionStep, PreSessionStepStatus, PreSessionStepType,
} from 'types';
import {dynamicContentUtils, imageUtils, sessionUtils} from 'utils';

import {AsyncRequestStep} from '../../PreSessionFlowWizard/steps/AsyncRequestStep';
import {FileUploadStep} from '../../PreSessionFlowWizard/steps/FileUploadStep';
import {FormStep} from '../../PreSessionFlowWizard/steps/FormStep';
import {ScreenshotStep} from '../../PreSessionFlowWizard/steps/ScreenshotStep';
import {LeaveSessionButton} from '../LeaveSessionButton';
import {PreSessionModeSwitcher} from '../PreSessionModeSwitcher';

const T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.PRE_SESSION_FLOW;

import './styles.scss';

const {isMatchElement, isMatchElementInTree, isMatchLanguage, nodeElementParent, renderDefaultNode} = dynamicContentUtils;

type StepTemplateProps = {
    stepContent: React.ReactNode;
};

// Mirrors the per-message-template pattern in ClientSessionTextChatView:
// render the root template subtree only; for the inner step-content unit substitute the React
// step component; everything outside the template subtree is hidden.
const buildStepTemplateOptionsGetter = (templateUnit: string) => (props: StepTemplateProps): HTMLReactParserOptions => {
    const {stepContent} = props;
    return {
        replace: (domNode: any) => {
            if (!domNode.attribs) {
                return null;
            }
            const node = domNode as ParserElement;

            if (isMatchElement(templateUnit, node)) {
                return nodeElementParent({show: true});
            }
            if (isMatchElement(Units.StepContent, node)) {
                return <NodeElement node={node}>{stepContent}</NodeElement>;
            }
            if (!isMatchLanguage(node)) {
                // eslint-disable-next-line react/jsx-no-useless-fragment
                return <Fragment />;
            }
            return nodeElementParent({show: isMatchElementInTree(templateUnit, node)});
        }
    };
};

const getFormStepTemplateOptions = buildStepTemplateOptionsGetter(Units.FormStepTemplate);
const getAsyncRequestStepTemplateOptions = buildStepTemplateOptionsGetter(Units.AsyncRequestStepTemplate);
const getScreenshotStepTemplateOptions = buildStepTemplateOptionsGetter(Units.ScreenshotStepTemplate);
const getFileUploadStepTemplateOptions = buildStepTemplateOptionsGetter(Units.FileUploadStepTemplate);

const getStepTemplateOptionsGetter = (stepType: PreSessionStepType) => {
    if (stepType === PreSessionStepType.Form) {
        return getFormStepTemplateOptions;
    }
    if (stepType === PreSessionStepType.AsyncRequest) {
        return getAsyncRequestStepTemplateOptions;
    }
    if (stepType === PreSessionStepType.Screenshot) {
        return getScreenshotStepTemplateOptions;
    }
    if (stepType === PreSessionStepType.FileUpload) {
        return getFileUploadStepTemplateOptions;
    }
    return null;
};

const renderStepContent = (
    step: PreSessionStep, sessionId: string, participantId: string, skipButton: React.ReactNode,
    onBusyChange: (busy: boolean) => void,
): React.ReactNode => {
    if (step.type === PreSessionStepType.Form) {
        return (<FormStep
            key={step.id}
            sessionId={sessionId}
            participantId={participantId}
            step={step}
            skipButton={skipButton}
            onBusyChange={onBusyChange}
        />);
    }
    if (step.type === PreSessionStepType.AsyncRequest) {
        return (
            <AsyncRequestStep
                sessionId={sessionId}
                participantId={participantId}
                step={step}
                skipButton={skipButton}
            />
        );
    }
    if (step.type === PreSessionStepType.FileUpload) {
        return (
            <FileUploadStep
                sessionId={sessionId}
                participantId={participantId}
                step={step}
                skipButton={skipButton}
            />
        );
    }
    if (step.type === PreSessionStepType.Screenshot) {
        return (
            <ScreenshotStep
                sessionId={sessionId}
                participantId={participantId}
                step={step}
                skipButton={skipButton}
            />
        );
    }
    return null;
};

type DynamicProps = {
    modeSwitchBusy: boolean;
    hideLeaveSessionButton: boolean;
    image?: string;
    step: PreSessionStep | null;
    stepContent: React.ReactNode;
    title: string;
    description: string;
    progressLabel: string;
    stepName: string;
    stepPrompt: string;
    skipStepTypeWrapper: boolean;
    endLocalSession: () => void;
    localParticipant: any;
};

const getOptions = (props: DynamicProps, template: string): HTMLReactParserOptions => {
    const {
        image, step, stepContent, title, description, progressLabel, stepName, stepPrompt,
        skipStepTypeWrapper, endLocalSession, localParticipant, modeSwitchBusy, hideLeaveSessionButton,
    } = props;
    const stepTemplateOptionsGetter = skipStepTypeWrapper || !step ? null : getStepTemplateOptionsGetter(step.type);
    return {
        replace: (domNode: any) => {
            if (!domNode.attribs) {
                return null;
            }
            const node = domNode as ParserElement;

            // Top-level step-type template definitions are hidden in the outer pass; they're
            // walked again by the nested DynamicContent render under StepContent.
            if (isMatchElement(Units.FormStepTemplate, node) ||
                isMatchElement(Units.AsyncRequestStepTemplate, node) ||
                isMatchElement(Units.ScreenshotStepTemplate, node)) {
                return nodeElementParent({show: false});
            }

            if (node.attribs.class?.split(/\s+/).includes('header')) {
                return (
                    <Fragment>
                        <NodeElement node={node}>{domToReact(node.children as ParserElement[], getOptions(props, template))}</NodeElement>
                        <PreSessionModeSwitcher busy={modeSwitchBusy} />
                    </Fragment>
                );
            }
            if (isMatchElement(Units.Logo, node)) {
                return (
                    <NodeElement node={node}>
                        <img className='logo-img' src={image || imageUtils.getLogo()} alt='Logo' />
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.Language, node)) {
                return <NodeElement node={node}><LanguageSwitcher className='language-switcher' hidePlaceholder /></NodeElement>;
            }
            if (isMatchElement(Units.Title, node)) {
                return <NodeElement node={node}>{title}</NodeElement>;
            }
            if (isMatchElement(Units.Description, node)) {
                return <NodeElement node={node}>{description}</NodeElement>;
            }
            if (isMatchElement(Units.StepProgress, node)) {
                return <NodeElement node={node}>{progressLabel}</NodeElement>;
            }
            if (isMatchElement(Units.StepName, node)) {
                return <NodeElement node={node}>{stepName}</NodeElement>;
            }
            if (isMatchElement(Units.StepPrompt, node)) {
                return <NodeElement node={node}>{stepPrompt}</NodeElement>;
            }
            if (isMatchElement(Units.StepContent, node)) {
                if (!stepTemplateOptionsGetter) {
                    return <NodeElement node={node}>{stepContent}</NodeElement>;
                }
                return (
                    <NodeElement node={node}>
                        <DynamicContent
                            template={template}
                            optionsProps={{stepContent}}
                            optionsGetter={stepTemplateOptionsGetter}
                        />
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.LeaveSessionButton, node) ||
                isMatchElement(Units.LeaveSessionButtonDefault, node)) {
                if (hideLeaveSessionButton) {
                    return nodeElementParent({show: false});
                }
                return (
                    <NodeElement node={node}>
                        <LeaveSessionButton onClick={endLocalSession} />
                    </NodeElement>
                );
            }
            return renderDefaultNode(node, {role: localParticipant?.role});
        }
    };
};

export const PreSessionFlowView: React.FC = () => {
    const {t} = useTranslation();
    const [formBusy, setFormBusy] = useState(false);
    const {endLocalSession} = useContext(ClientContext);
    const {session, image, localParticipant, textChatActivity, avatarActivity} = useClientSession();

    const flowState = useMemo<PreSessionFlowState | null>(() => {
        // Look at the active default activity (TextChat or Avatar) for the snapshot.
        const activity = textChatActivity || avatarActivity;
        const data = activity?.data as {preSessionFlow?: PreSessionFlowState} | undefined;
        return data?.preSessionFlow ?? null;
    }, [textChatActivity, avatarActivity]);

    const currentStep = useMemo(() => {
        if (!flowState) {
            return null;
        }
        return flowState.flowSnapshot.steps[flowState.currentStepIndex] ?? null;
    }, [flowState]);

    const currentState = useMemo(() => {
        if (!currentStep) {
            return null;
        }
        return flowState.steps.find((s) => s.stepId === currentStep.id) ?? null;
    }, [flowState, currentStep]);

    const sessionId = session?.id;
    const participantId = useMemo(() => sessionUtils.getClientParticipant(session)?.id, [session]);

    const isAwaitingConfirmation = !!flowState?.awaitingConfirmation;

    const {send: confirmReady, loading: confirming} = useApiData(
        clientActionsApi.confirmPreSessionFlow,
        {notificationType: PredefinedNotifierType.SaveError},
    );

    const {send: skipStep, loading: skipping} = useApiData(
        clientActionsApi.skipPreSessionStep,
        {notificationType: PredefinedNotifierType.SaveError},
    );

    const handleConnect = useCallback(() => {
        if (!sessionId || !participantId) {
            return;
        }
        confirmReady({sessionId, participantId});
    }, [confirmReady, sessionId, participantId]);

    const handleSkip = useCallback(() => {
        if (!sessionId || !participantId || !currentStep) {
            return;
        }
        skipStep({sessionId, participantId}, {stepId: currentStep.id});
    }, [skipStep, sessionId, participantId, currentStep]);

    const endsSession = useMemo(() => [PreSessionFlowCompletion.End, PreSessionFlowCompletion.Chat]
        .includes(flowState?.flowSnapshot.onComplete), [flowState?.flowSnapshot.onComplete]);

    const stepContent = useMemo(() => {
        if (isAwaitingConfirmation) {
            return (
                <div className='tw-flex tw-justify-center'>
                    <Button color='primary' loading={confirming} onClick={handleConnect}>
                        {t(endsSession ? T_KEYS.END_SESSION : T_KEYS.CONNECT)}
                    </Button>
                </div>
            );
        }
        if (!currentStep || !currentState || !sessionId || !participantId) {
            return null;
        }
        if (currentState.status !== PreSessionStepStatus.InProgress) {
            return null;
        }
        // Required async steps become skippable only after exhausting retries (keeps the client
        // from being trapped on a geolocation/SUMA verification that keeps failing).
        const retriesExhausted = currentStep.type === PreSessionStepType.AsyncRequest
            && currentState.attempts >= PRE_SESSION_FLOW_LIMITS.PER_STEP_MAX_RETRIES;
        const canSkip = !currentStep.required || retriesExhausted;
        // Skip is the lowest-emphasis action — give it an icon so it reads differently from the
        // outlined secondary action (e.g. Retake) it can sit beside.
        const skipButton = canSkip ? (
            <Button
                color='default'
                icon='chevron-right'
                disabled={skipping}
                loading={skipping}
                onClick={handleSkip}
            >
                {t(T_KEYS.SKIP)}
            </Button>
        ) : null;
        return renderStepContent(currentStep, sessionId, participantId, skipButton, setFormBusy);
    }, [isAwaitingConfirmation, confirming, handleConnect, currentStep, currentState, sessionId,
        participantId, t, skipping, handleSkip, endsSession]);

    const title = useMemo(
        () => (isAwaitingConfirmation ? t(T_KEYS.READY_TITLE) : t(T_KEYS.TITLE)),
        [isAwaitingConfirmation, t],
    );
    const description = useMemo(
        () => (isAwaitingConfirmation ? t(endsSession ? T_KEYS.COMPLETED_DESCRIPTION : T_KEYS.READY_DESCRIPTION) : t(T_KEYS.DESCRIPTION)),
        [isAwaitingConfirmation, endsSession, t],
    );
    const progressLabel = useMemo(() => {
        if (!flowState || isAwaitingConfirmation) {
            return '';
        }
        const total = flowState.flowSnapshot.steps.length;
        if (total <= 1) {
            return '';
        }
        return t(T_KEYS.PROGRESS, {current: flowState.currentStepIndex + 1, total});
    }, [flowState, isAwaitingConfirmation, t]);

    const stepName = useMemo(
        () => (isAwaitingConfirmation || !currentStep ? '' : currentStep.name),
        [isAwaitingConfirmation, currentStep],
    );
    const stepPrompt = useMemo(
        () => (isAwaitingConfirmation || !currentStep ? '' : currentStep.clientPrompt),
        [isAwaitingConfirmation, currentStep],
    );

    if (!currentStep && !isAwaitingConfirmation) {
        return null;
    }

    return (
        <ClientDynamicContent
            component={DynamicComponentNames.PreSessionFlow}
            defaultTemplate={PRE_SESSION_FLOW_DEFAULT_TEMPLATE}
            optionsProps={{
                modeSwitchBusy: formBusy || skipping,
                hideLeaveSessionButton: isAwaitingConfirmation && endsSession,
                image,
                step: currentStep,
                stepContent,
                title,
                description,
                progressLabel,
                stepName,
                stepPrompt,
                skipStepTypeWrapper: isAwaitingConfirmation,
                endLocalSession,
                localParticipant,
            }}
            optionsGetter={getOptions}
        />
    );
};
